import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaShoppingCart, FaDollarSign, FaCalendarCheck, FaChartLine, FaUtensils } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getReceiptsSummaryByLocalIdAndClientId } from '../../../../../../../../crud/clients_api/client_receipts';
import { ClientLocal } from '../../../../types/client.types';
import LoadingSpinner from '../../../../../../../../components/common/LoadingSpinner';
import './ClientDetailsModal.scss';

interface ClientDetailsModalProps {
  show: boolean;
  handleClose: () => void;
  client: ClientLocal;
  localId: number;
}

interface Receipt {
  receipt_id: number;
  selections: any;
  total_payed: number;
  timestamp: string;
  local_timestamp: string;
  review: any | null;
}

interface ProductStat {
  item_name: string;
  category_name: string;
  total_quantity: number;
  total_spent: number;
  times_ordered: number;
}

const ClientDetailsModal: React.FC<ClientDetailsModalProps> = ({
  show,
  handleClose,
  client,
  localId
}) => {
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchClientReceipts = async () => {
    setLoading(true);
    try {
      const response = await getReceiptsSummaryByLocalIdAndClientId(localId, client.client_id);
      setReceipts(response.data || []);
    } catch (error: any) {
      console.error('Error fetching client receipts:', error);
      toast.error('Error al cargar los datos del cliente');
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && client.client_id) {
      fetchClientReceipts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, client.client_id, localId]);

  // Aggregate product statistics from selections
  const productStats = useMemo(() => {
    const productMap = new Map<string, ProductStat>();

    receipts.forEach(receipt => {
      if (!receipt.selections || !Array.isArray(receipt.selections)) return;

      // selections is an array of Selection objects
      receipt.selections.forEach((selection: any) => {
        if (!selection) return;

        const itemName = selection.item_name || 'Producto desconocido';
        const categoryName = selection.category_name || 'Sin categoría';
        const quantity = selection.quantity || 1;
        const price = selection.total_price || selection.price || 0;

        const key = `${itemName}_${categoryName}`;
        
        if (productMap.has(key)) {
          const existing = productMap.get(key)!;
          existing.total_quantity += quantity;
          existing.total_spent += price;
          existing.times_ordered += 1;
        } else {
          productMap.set(key, {
            item_name: itemName,
            category_name: categoryName,
            total_quantity: quantity,
            total_spent: price,
            times_ordered: 1
          });
        }
      });
    });

    return Array.from(productMap.values()).sort((a, b) => b.total_spent - a.total_spent);
  }, [receipts]);

  // Financial statistics
  const financialStats = useMemo(() => {
    const totalSpent = receipts.reduce((sum, r) => sum + (r.total_payed || 0), 0);
    const visitCount = receipts.length;
    const averagePerVisit = visitCount > 0 ? totalSpent / visitCount : 0;

    // Calculate visit frequency (days between visits)
    const sortedReceipts = [...receipts].sort((a, b) => 
      new Date(a.timestamp || a.local_timestamp).getTime() - 
      new Date(b.timestamp || b.local_timestamp).getTime()
    );

    let totalDaysBetweenVisits = 0;
    let visitGaps = 0;

    for (let i = 1; i < sortedReceipts.length; i++) {
      const prevDate = new Date(sortedReceipts[i - 1].timestamp || sortedReceipts[i - 1].local_timestamp);
      const currDate = new Date(sortedReceipts[i].timestamp || sortedReceipts[i].local_timestamp);
      const daysDiff = (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
      totalDaysBetweenVisits += daysDiff;
      visitGaps++;
    }

    const avgDaysBetweenVisits = visitGaps > 0 ? totalDaysBetweenVisits / visitGaps : 0;

    return {
      totalSpent,
      visitCount,
      averagePerVisit,
      avgDaysBetweenVisits
    };
  }, [receipts]);

  const formatCurrency = (value: number) => 
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const renderStatCard = (icon: React.ReactNode, value: string | number, label: string) => (
    <div className="stat-card">
      <div className="stat-header">
        <div className="stat-icon">{icon}</div>
        <div className="stat-value">{value}</div>
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );

  const renderContent = () => {
    if (loading) {
      return <LoadingSpinner text="Cargando datos del cliente..." />;
    }

    if (receipts.length === 0) {
      return (
        <div className="no-data">
          <FaShoppingCart />
          <p>Este cliente aún no tiene compras registradas</p>
        </div>
      );
    }

    return (
      <div className="client-details-content">
        {/* Financial Overview */}
        <section className="details-section">
          <h4 className="section-title">
            <FaDollarSign />
            Resumen Financiero
          </h4>
          <div className="stats-grid">
            {renderStatCard(
              <FaDollarSign />,
              formatCurrency(financialStats.totalSpent),
              'Total Gastado'
            )}
            {renderStatCard(
              <FaCalendarCheck />,
              financialStats.visitCount.toString(),
              'Total de Visitas'
            )}
            {renderStatCard(
              <FaChartLine />,
              formatCurrency(financialStats.averagePerVisit),
              'Promedio por Visita'
            )}
            {renderStatCard(
              <FaCalendarCheck />,
              financialStats.avgDaysBetweenVisits > 0 
                ? `Cada ${Math.round(financialStats.avgDaysBetweenVisits)} días`
                : 'N/A',
              'Frecuencia de Visita'
            )}
          </div>
        </section>

        {/* Product Insights */}
        <section className="details-section">
          <h4 className="section-title">
            <FaUtensils />
            Productos Favoritos
          </h4>
          <div className="products-list">
            {productStats.slice(0, 5).map((product, idx) => (
              <div key={idx} className="product-item">
                <div className="product-rank">#{idx + 1}</div>
                <div className="product-info">
                  <div className="product-name">{product.item_name}</div>
                  <div className="product-category">{product.category_name}</div>
                </div>
                <div className="product-stats">
                  <div className="product-quantity">{product.total_quantity}x</div>
                  <div className="product-spent">{formatCurrency(product.total_spent)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Last Visits */}
        <section className="details-section">
          <h4 className="section-title">
            <FaCalendarCheck />
            Últimas Visitas
          </h4>
          <div className="visits-list">
            {receipts.slice(0, 10).map((receipt, idx) => (
              <div key={idx} className="visit-item">
                <div className="visit-date">
                  {formatDate(receipt.timestamp || receipt.local_timestamp)}
                </div>
                <div className="visit-amount">
                  {formatCurrency(receipt.total_payed)}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="lg" 
      centered
      className="client-details-modal"
    >
      <Modal.Header>
        <div className="form-modal-title-container">
          <h2 className="form-modal-title">
            Detalles del Cliente
          </h2>
          <div className="client-header-info">
            <span className="client-name">{client.username} {client.last_name || ''}</span>
            {client.email && <span className="client-email">{client.email}</span>}
          </div>
        </div>
        <button className="modal-close-button" onClick={handleClose}>
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body>
        {renderContent()}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <button className="btn btn-secondary" onClick={handleClose}>
            Cerrar
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ClientDetailsModal;
